/** Chart rendering utilities — shared across all chart types */

import { useRef, useState, useEffect } from "react";

/** Scale helper — maps design-basis pixel values to current resolution */
export const px = (base: number, S: number) => Math.round(base * S);

/** Embed chart max display width on PC (CSS px); mobile keeps full responsive width. */
const CHART_MAX_CSS_WIDTH_PC = 500;

/** Map design width to CSS px, capping at CHART_MAX_CSS_WIDTH_PC on PC. */
export const chartCssWidth = (
  designWidth: number,
  S: number,
  isPC: boolean,
): number => {
  const css = px(designWidth, S);
  return isPC ? Math.min(css, CHART_MAX_CSS_WIDTH_PC) : css;
};

/**
 * Measure the rendered width of a container element via ResizeObserver.
 * Returns [ref, widthInPixels]. Attach ref to a `width:100%` wrapper div.
 */
export function useAvailableWidth(): [
  React.RefObject<HTMLDivElement | null>,
  number,
] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 0) {
        setWidth(w);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return [ref, width];
}

/**
 * Font family — General Sans as primary (matches Figma spec),
 * with design-system CSS variable fallback for web rendering.
 */
export const FONT =
  "'General Sans', var(--font-title_xl, 'Satoshi'), sans-serif";

/** Parse unit string into prefix/suffix for value display */
function parseUnit(unit: string): { prefix: string; suffix: string } {
  if (unit.startsWith("$")) {
    return { prefix: "$", suffix: unit.slice(1) };
  }
  return { prefix: "", suffix: unit };
}

/**
 * Format a numeric value for chart display.
 * - Supports K/M/B suffixes for large numbers
 * - Rounds to 2 decimal places, removes trailing zeros
 * - Handles unit prefix ($) and suffix (%, etc.)
 *
 * Examples:
 *   formatChartValue(19450, "")      → "19.45K"
 *   formatChartValue(1234567, "$")   → "$1.23M"
 *   formatChartValue(0.856, "%")     → "0.86%"
 *   formatChartValue(42, "")         → "42"
 *   formatChartValue(3.10, "")       → "3.1"
 */
export function formatChartValue(value: number, unit?: string): string {
  const { prefix, suffix } = parseUnit(unit || "");

  const abs = Math.abs(value);
  let num: string;

  if (abs >= 1_000_000_000) {
    num = trimTrailingZeros((value / 1_000_000_000).toFixed(2)) + "B";
  } else if (abs >= 1_000_000) {
    num = trimTrailingZeros((value / 1_000_000).toFixed(2)) + "M";
  } else if (abs >= 1_000) {
    num = trimTrailingZeros((value / 1_000).toFixed(2)) + "K";
  } else if (Number.isInteger(value)) {
    num = String(value);
  } else {
    num = trimTrailingZeros(value.toFixed(2));
  }

  return `${prefix}${num}${suffix}`;
}

function trimTrailingZeros(s: string): string {
  if (!s.includes(".")) {
    return s;
  }
  return s.replace(/\.?0+$/, "");
}

export interface YAxisConfig {
  yMin: number;
  yMax: number;
  ticks: number[];
  hasNeg: boolean;
}

/**
 * Compute Y-axis range and tick values with two modes:
 * - Negative values present → ticks symmetric around 0 (equal distance above & below)
 * - All non-negative → no negative axis rendered; bar charts anchor at 0
 */
export function computeYAxis(
  values: number[],
  opts: { tickCount?: number; includeZero?: boolean } = {},
): YAxisConfig {
  const { tickCount = 6, includeZero = false } = opts;
  const rawMin = Math.min(...values);
  const rawMax = Math.max(...values);
  const hasNeg = rawMin < 0;

  if (hasNeg) {
    const extent = Math.max(Math.abs(rawMin), Math.abs(rawMax));
    const paddedExtent = extent * 1.15;
    const halfCount = Math.floor(tickCount / 2);
    const step = paddedExtent / halfCount;
    const ticks: number[] = [];
    for (let i = -halfCount; i <= halfCount; i++) {
      ticks.push(i * step);
    }
    return { yMin: -paddedExtent, yMax: paddedExtent, ticks, hasNeg: true };
  }

  if (includeZero) {
    const paddedMax = (rawMax || 1) * 1.15;
    const step = paddedMax / (tickCount - 1);
    const ticks: number[] = [];
    for (let i = 0; i < tickCount; i++) {
      ticks.push(i * step);
    }
    return { yMin: 0, yMax: paddedMax, ticks, hasNeg: false };
  }

  const range = rawMax - rawMin || 1;
  const yPadding = range * 0.15;
  const yMin = Math.max(0, rawMin - yPadding);
  const yMax = rawMax + yPadding;
  const step = (yMax - yMin) / (tickCount - 1);
  const ticks: number[] = [];
  for (let i = 0; i < tickCount; i++) {
    ticks.push(yMin + step * i);
  }
  return { yMin, yMax, ticks, hasNeg: false };
}

/**
 * Estimate rendered width of text in SVG at the given font size.
 * CJK characters are treated as full-width; ASCII as ~0.6 em.
 */
export function estimateTextWidth(text: string, fontSize: number): number {
  let width = 0;
  for (const char of text) {
    width += char.charCodeAt(0) > 0x2e80 ? fontSize * 1.1 : fontSize * 0.65;
  }
  return width;
}
